import DataTable, { TableColumn } from "react-data-table-component";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import EditStaff from "./edit-staff";
import classNames from "classnames";
import { TrashSvg } from "../../icons";
import {
  ApiResponse,
  IStaff,
  IStaffAuthTokenRequest,
  IDepartment,
  ApiErrorResponse,
} from "../../utils/types";
import useGetStaff from "../../hooks/use-get-staff";
import { FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import Switch from "../../components/common/Switch";
import { queryClient } from "../../App";
import Modal from "../../components/common/Modal";
import { errorToast, successToast } from "../../utils/helpers";
import useDeleteStaff from "../../hooks/use-delete-staff";
import useUpdateStaff from "../../hooks/use-update-staff";
import Button from "../../components/common/button";
import Spinner from "../../components/common/Spinner";
import { useGetUserPermission } from "../../context/AuthStaffUserContext";

type FormValues = {
  statuses: Record<string, boolean>;
};

const ITEMS_PER_PAGE = 10;

const CurrentStaff = ({
  departmentsData,
}: {
  departmentsData: IDepartment[];
}) => {
  const methods = useForm<FormValues>();
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<string | null>(null);
  const [perPage, setPerPage] = useState(ITEMS_PER_PAGE);
  const { mutate: deleteStaff, isPending: isDeleting } = useDeleteStaff();
  const { mutate: updateStaff, isPending: isUpdating } = useUpdateStaff("");

  // Get StaffAuthData
  const staffAuthData: ApiResponse<IStaffAuthTokenRequest> | undefined =
    queryClient.getQueryData(["auth-user"]);
  const { write: isEditPermission } = useGetUserPermission("tv-app");

  const {
    data: staffData,
    isLoading,
    isError,
  } = useGetStaff({ page: currentPage, perPage });

  if (isLoading) {
    return <Spinner fullScreenOverlay className="z-[100]" />;
  }

  if ((!isLoading && !staffData) || isError) {
    return <h1>Something went wrong</h1>;
  }

  const staffList = (staffData?.items || []).filter((staff) => staff);
  const totalItems = staffData?.total || 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleStatusChange = (row: IStaff, newStatus: boolean) => {
    if (!row._id) return;
    row.isEnable = newStatus;
    updateStaff(
      {
        isEnable: newStatus,
        id: row._id,
      },
      {
        onSuccess: (res) => {
          successToast(res.result.message);
        },
        onError: (err: ApiErrorResponse) => {
          errorToast(
            err?.response?.data?.result?.message ||
              "Failed to update staff status"
          );
        },
      }
    );
  };

  const handleDeleteStaff = (staffId: string) => {
    deleteStaff(staffId, {
      onSuccess: (res) => {
        successToast(res.result.message);
        setDeleteModalOpen(false);
        setStaffToDelete(null);
      },
      onError: (err: Error) => {
        errorToast(err?.message || "Failed to delete staff");
      },
    });

    setDeleteModalOpen(false);
  };

  const tableColumns: Array<TableColumn<IStaff>> = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "15%",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      width: "30%",
    },
    {
      name: "Department",
      cell: (row) => {
        return row.department.map((dept) => dept.departmentName).join(", ");
      },
      sortable: true,
      width: "20%",
    },
    {
      name: "Status",
      sortable: true,
      width: "20%",
      cell: (row) => (
        <Switch
          name={row._id}
          checked={row.isEnable}
          disabled={
            row.email === staffAuthData?.result.data.email ||
            isUpdating ||
            !isEditPermission
          }
          onCheck={(checked) => {
            handleStatusChange(row, !checked);
          }}
        />
      ),
    },
    {
      name: "Action",
      width: "15%",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <EditStaff
            departmentsData={departmentsData}
            data={row}
            disabled={!isEditPermission}
            currentStaffAuthMail={staffAuthData?.result.data.email!}
          />
          <div
            onClick={() => {
              if (isEditPermission && row._id) {
                setStaffToDelete(row._id);
                setDeleteModalOpen(true);
              }
            }}
            className={classNames(
              "h-6 w-6 rounded-[3.63px] bg-[#E5272733] flex items-center justify-center cursor-pointer",
              !isEditPermission
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
            )}
          >
            <TrashSvg color="#E52727" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form>
        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-6"
            columns={tableColumns}
            data={staffList}
            progressPending={isLoading}
            pagination
            paginationServer
            paginationTotalRows={totalItems}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            persistTableHead
          />
        </DndProvider>
      </form>
      {deleteModalOpen && (
        <Modal
          isOpen={deleteModalOpen}
          closeModal={() => setDeleteModalOpen(false)}
          title="Are you sure to delete this staff?"
          className="w-144.75"
        >
          <div className="flex items-center justify-end gap-[18px] mt-9">
            <Button
              className="w-25.5"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="w-25.5"
              onClick={() => {
                if (staffToDelete) {
                  handleDeleteStaff(staffToDelete);
                }
              }}
              variant="primary"
              disabled={isDeleting}
            >
              Delete
            </Button>
          </div>
        </Modal>
      )}
      {isLoading && <Spinner fullScreenOverlay className="z-[100]" />}
      {isDeleting && <Spinner fullScreenOverlay className="z-[100]" />}
      {isUpdating && <Spinner fullScreenOverlay className="z-[100]" />}
    </FormProvider>
  );
};

export default CurrentStaff;
